<template>
  <v-dialog :value="value" persistent max-width="600px">
    <nv-form
      v-if="item != null"
      :errors="formErrors"
      :title="formTitle"
      :loading="loading"
      data-cy="brand-channel-form"
      @cancel="formCancel"
      @nv-submit="formSubmit"
    >
      <template #title-extend>
        <v-spacer />
        <v-btn v-if="channel && brand" text outlined @click="useBrandValues">
          Use Brand Values
        </v-btn>
      </template>
      <v-row>
        <v-col
          v-if="
            ((countryCodeProp === undefined && countryCode !== '*') ||
              (countryCodeProp !== undefined && countryCodeProp !== '*')) &&
              canViewDefaultTreeIdDropdown()
          "
          cols="12"
        >
          <v-row>
            <v-col cols="12" class="mt-2">
              <tree-search
                v-if="value"
                v-model="item.default_tree_id"
                label="Default Tree ID (Search)"
                mode="classification"
                country-code="*"
                skip-purpose
                @channel-meta-data="handleChannelType"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-text-field
            ref="name"
            v-model="item.name"
            data-cy="name"
            :rules="rules.name"
            label="Name"
            counter="500"
          />
        </v-col>
        <v-col v-if="editing || cls" cols="12">
          <v-row>
            <v-col cols="12" md="8">
              <tree-search
                v-if="item[`${parentEntityType}_id`] || (cls && value)"
                v-model="parentEntityId"
                :mode="parentEntityType"
                :rules="rules.tree"
                :country-code="editingCountryCode"
                @name-change="handleParentNameChange"
              />
            </v-col>
            <v-col cols="12" md="4">
              <div class="d-flex align-center">
                <v-icon class="mr-5">mdi-filter-variant</v-icon>
                <v-select
                  v-model="editingCountryCode"
                  :items="countries"
                  label="Country"
                  :disabled="editing || processed || loading"
                  @change="onCountryChange"
                ></v-select>
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" sm="6">
          <ad-supported v-model="item.ad_supported" />
        </v-col>
        <v-col cols="12" sm="6">
          <status-selector v-model="item.status" />
        </v-col>
        <v-col cols="12">
          <subcategory-selector
            v-if="value"
            v-model="item.sub_category_id"
            :editing="item.sub_category"
            :entity-name="item.name"
            :context="contextWithParentName"
          />
        </v-col>
        <v-col :sm="editing ? 6 : 12" cols="12">
          <owl-date-picker v-model="item.start_date" :clearable="!editing" label="Start Date" />
        </v-col>
        <v-col v-if="editing" cols="12" sm="6">
          <owl-date-picker v-model="item.end_date" :start-date="item.start_date" label="End Date" />
        </v-col>
        <v-col v-if="editing" cols="12">
          <v-text-field v-model="item.reason" label="Reason" />
        </v-col>
      </v-row>
    </nv-form>
  </v-dialog>
</template>

<script>
import HTTP from '@/api/owl';
import AdSupported from '@/components/mv/AdSupported';
import StatusSelector from '@/components/mv/StatusSelector';
import Rules from 'bsm-validation-rules';
import SubcategorySelector from '@/components/mv/SubcategorySelector';
import { FormMixin } from '@nielsen-media/ui-toolkit-vue-api';
import TreeSearch from '@/components/mv/TreeSearch';
import OwlDatePicker from '@/components/OwlDatePicker';
import { getConfigMap } from '@/helpers/filters';
import { notifyBrandChange, notifyChannelChange } from '@/api/mav-hierarchy-feedback';

const rules = {
  name: [Rules.presence(), Rules.length({ max: 500 })],
  tree: [Rules.presence()],
};

export default {
  components: { OwlDatePicker, TreeSearch, SubcategorySelector, StatusSelector, AdSupported },
  mixins: [FormMixin],
  props: {
    channel: {
      type: Boolean,
      default: false,
    },
    postEndpoint: {
      type: String,
      default: null,
    },
    cls: {
      type: Boolean,
      default: false,
    },
    brand: {
      type: Object,
      default: null,
    },
    countryCodeProp: {
      type: String,
      default: undefined,
    },
    prefill: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    item: {},
    selectedChannelMetaData: null,
    rules,
    countries: [
      { text: 'default', value: '*' },
      { text: 'us', value: 'us' },
    ],
    editingCountryCode: '*',
    parentEntityName: '',
    countryCode: '*',
  }),
  computed: {
    parentEntityType() {
      return this.channel ? 'brand' : 'parent';
    },
    parentEntityId: {
      get() {
        return this.item[`${this.parentEntityType}_id`];
      },
      set(value) {
        if (value !== undefined) {
          this.item[`${this.parentEntityType}_id`] = value;
        }
      },
    },
    contextWithParentName() {
      return {
        ...this.item,
        parent_name: this.parentEntityName,
        brand_name: this.parentEntityName,
      };
    },
  },
  watch: {
    editing: {
      immediate: true,
      handler(isEditing) {
        if (isEditing && this.item.country_codes && this.item.country_codes.length > 0) {
          this.editingCountryCode = this.item.country_codes[0];
        }
      },
    },
  },
  mounted() {
    this.countryCode = this.$route.query.countryCode
      ? this.$route.query.countryCode
      : this.countryCodeProp;
  },
  methods: {
    canViewDefaultTreeIdDropdown() {
      const config = this.getConfigMap();
      return config['defaultTreeIdDropdownFeature_owl_ui']?.enabled || false;
    },
    handleChannelType(channelMetaData) {
      this.selectedChannelMetaData = channelMetaData;
      this.fetchTypeData();
    },
    fetchTypeData() {
      const apiPath = `/${this.selectedChannelMetaData.type}s/${this.selectedChannelMetaData.id}`;
      this.loading = true;
      HTTP.get(apiPath)
        .then(response => {
          if (!response) {
            throw new Error(`Invalid response obtained ${response}.`);
          }
          if (!response.data) {
            throw new Error(`No data present in response object.`);
          }
          if (response.status !== 200) {
            throw new Error(`Getting response code as ${response.status}.`);
          }

          const { id, name, ad_supported, status, sub_category } = response.data;

          // Setting the item prop for pre-filling the form data
          this.item.name = name;
          this.item.ad_supported = ad_supported;
          this.item.status = status;
          this.item.sub_category_id = sub_category.id;
          this.item.replacement_tree_id = id;
          this.item.sub_category = sub_category;
        })
        .catch(error => {
          this.$flash('error', `Failed to pre-fill data into form ${error}`);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formErrorHandler(err) {
      const errors = err.response?.data?.errors;
      if (err.response?.status === 400 && errors) {
        this.formErrors = errors;
      } else if (err.response?.status === 422 && errors) {
        const messages = Object.entries(errors)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('; ');
        this.$flash('error', messages);
      } else {
        const status = err.response?.status;
        const msg = status
          ? `Request failed with status code ${status}`
          : 'Request failed. Please check your connection and try again.';
        this.$flash('error', msg);
      }
    },
    formSave() {
      if (this.item.sub_category) {
        // Removing sub_category field since not present in onSubmit API call
        // but need to have it to pre-fill the data in modal
        delete this.item.sub_category;
      }

      let path;
      if (this.item.country_codes) {
        this.item.country_code = this.item.country_codes[0];
      }

      // Remove default_tree_id since not needed now
      if (this.item.default_tree_id) {
        delete this.item.default_tree_id;
      }

      if (this.editing != null) {
        path = this.channel ? `/channels` : `/brands`;
      } else {
        path = this.cls ? `/brand/${this.item.brand_id}/channel` : this.postEndpoint;
      }

      const isChannel = this.channel;

      return this.submitForm(HTTP, path, {
        success: ({ data }) => {
          this.$flash('success', `${data.name} ${this.formContext} ${this.formSuccessMsg}`);
          // Send MAV hierarchy feedback
          if (data && data.item) {
            if (isChannel) {
              notifyChannelChange(data.item.id);
            } else {
              notifyBrandChange(data.item.id);
            }
          }
        },
      });
    },
    formReady() {
      this.formContext = this.channel ? 'Channel' : 'Brand';
      if (this.editing && this.item.country_codes && this.item.country_codes.length > 0) {
        this.editingCountryCode = this.item.country_codes[0];
      }
      if (this.prefill && !this.editing) {
        if (this.prefill.name) this.item.name = this.prefill.name;
        if (this.prefill.brand_id) this.item.brand_id = this.prefill.brand_id;
        if (this.prefill.parent_id) this.item.parent_id = this.prefill.parent_id;
        if (this.prefill.ad_supported) this.item.ad_supported = this.prefill.ad_supported;
        if (this.prefill.sub_category_id) {
          this.item.sub_category_id = this.prefill.sub_category_id;
          this.item.sub_category = {
            id: this.prefill.sub_category_id,
            name: this.prefill.sub_category_name || '',
            category: {
              id: this.prefill.category_id || null,
              name: this.prefill.category_name || '',
            },
          };
        }
        if (this.prefill.status) this.item.status = this.prefill.status;
      }
    },
    useBrandValues() {
      this.item = { ...this.brand, name: undefined, id: undefined };
    },
    onCountryChange(newCountry) {
      this.editingCountryCode = newCountry;
    },
    handleParentNameChange(name) {
      this.parentEntityName = name || '';
    },
    getConfigMap,
  },
};
</script>
